import supabase from './supabaseClient';
import { getDefaultPermissions } from '../models/permissions';

// Fetch all users
export const getAllUsers = async () => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      return { success: false, error };
    }

    // Map les noms de colonnes snake_case vers camelCase
    const mappedData = data?.map(user => ({
      id: user.id,
      email: user.email,
      firstName: user.first_name,
      lastName: user.last_name,
      password: user.password,
      role: user.role,
      status: user.status,
      permissions: user.permissions || getDefaultPermissions(user.role),
      lastLogin: user.last_login,
      createdAt: user.created_at,
      updatedAt: user.updated_at
    })) || [];

    return { success: true, data: mappedData };
  } catch (error) {
    return { success: false, error };
  }
};

// Fetch single user
export const getUser = async (id) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      return { success: false, error };
    }

    // Map les noms de colonnes snake_case vers camelCase
    const mappedUser = {
      id: data.id,
      email: data.email,
      firstName: data.first_name,
      lastName: data.last_name,
      password: data.password,
      role: data.role,
      status: data.status,
      permissions: data.permissions || getDefaultPermissions(data.role),
      lastLogin: data.last_login,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };

    return { success: true, data: mappedUser };
  } catch (error) {
    return { success: false, error };
  }
};

// Create new user
export const createUser = async (userData) => {
  try {
    const newUser = {
      id: `user-${Date.now()}`,
      email: userData.email,
      first_name: userData.firstName,
      last_name: userData.lastName,
      password: userData.password,
      role: userData.role || 'operator',
      status: userData.status || 'active',
      permissions: userData.permissions || getDefaultPermissions(userData.role || 'operator'),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    };

    const { data, error } = await supabase
      .from('users')
      .insert([newUser])
      .select()
      .single();

    if (error) {
      return { success: false, error };
    }

    // Map les noms de colonnes snake_case vers camelCase
    const mappedUser = {
      id: data.id,
      email: data.email,
      firstName: data.first_name,
      lastName: data.last_name,
      password: data.password,
      role: data.role,
      status: data.status,
      permissions: data.permissions || getDefaultPermissions(data.role),
      lastLogin: data.last_login,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };

    return { success: true, data: mappedUser };
  } catch (error) {
    return { success: false, error };
  }
};

// Update user
export const updateUser = async (id, userData) => {
  try {
    // Map les données en formats snake_case pour la base de données
    const updateData = {
      email: userData.email,
      first_name: userData.firstName,
      last_name: userData.lastName,
      password: userData.password || undefined,
      role: userData.role,
      status: userData.status,
      permissions: userData.permissions,
      updated_at: new Date().toISOString()
    };
    
    // Supprimer les propriétés undefined
    Object.keys(updateData).forEach(key => updateData[key] === undefined && delete updateData[key]);

    const { data, error } = await supabase
      .from('users')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      return { success: false, error };
    }

    // Map les noms de colonnes snake_case vers camelCase
    const mappedUser = {
      id: data.id,
      email: data.email,
      firstName: data.first_name,
      lastName: data.last_name,
      password: data.password,
      role: data.role,
      status: data.status,
      permissions: data.permissions || getDefaultPermissions(data.role),
      lastLogin: data.last_login,
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };

    return { success: true, data: mappedUser };
  } catch (error) {
    return { success: false, error };
  }
};

// Delete user
export const deleteUser = async (id) => {
  try {
    const { error } = await supabase
      .from('users')
      .delete()
      .eq('id', id);

    if (error) {
      return { success: false, error };
    }

    return { success: true };
  } catch (error) {
    return { success: false, error };
  }
};

// Check if email exists
export const checkEmailExists = async (email, excludeId = null) => {
  try {
    let query = supabase
      .from('users')
      .select('id')
      .eq('email', email);

    if (excludeId) {
      query = query.neq('id', excludeId);
    }

    const { data, error } = await query;

    if (error) {
      return { success: false, error };
    }

    return { success: true, exists: data && data.length > 0 };
  } catch (error) {
    return { success: false, error };
  }
};

// Update user permissions
export const updateUserPermissions = async (id, permissions) => {
  try {
    const { data, error } = await supabase
      .from('users')
      .update({ permissions })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      return { success: false, error };
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error };
  }
};
